import { el, progress, example, resultCard } from '../dom.js';
import { copy } from '../copy.js';
import { createCaptureInput, fileToObjectURL, revokeObjectURL } from '../camera.js';

// Multi-photo loop: user can add any number of access points / mesh nodes.
// Each goes through: take photo -> review -> upload -> recognition result
// -> "Add this one" (commits to list) or "Retake".

export function renderAp(state, actions) {
  const { goto, submitPhoto, clearError, clearSlot, commitPendingAp } = actions;

  let pendingFile = null;
  let pendingUrl = null;
  let pendingRoom = '';
  const pendingAp = state.photos.pendingAp;
  const showExample = !pendingFile && !pendingAp && state.photos.aps.length === 0;

  const screen = el('div', { class: 'screen' });
  screen.appendChild(progress({ step: 3, total: 4 }));
  screen.appendChild(el('h1', { text: copy.ap.title }));
  screen.appendChild(el('p', { text: copy.ap.body }));
  if (copy.ap.stickerHint) {
    screen.appendChild(el('p', { class: 'hint', text: copy.ap.stickerHint }));
  }

  // Already-committed APs: show chips up top so the user can see progress.
  if (state.photos.aps.length > 0) {
    const chips = el('div', { class: 'captured' });
    for (const ap of state.photos.aps) {
      const label = [ap.room, ap.extraction?.vendor, ap.extraction?.model]
        .filter(Boolean)
        .join(' · ');
      chips.appendChild(
        el('span', { class: 'chip' }, [
          el('span', { class: 'check', text: '✓' }),
          ` ${label || 'Got it'}`,
        ]),
      );
    }
    screen.appendChild(chips);
  }

  if (showExample) {
    screen.appendChild(
      example({
        label: copy.ap.exampleLabel,
        src: '/assets/examples/ap-example.jpg',
        caption: copy.ap.exampleCaption,
      }),
    );
  }

  const bodyWrap = el('div', {});
  screen.appendChild(bodyWrap);

  const errorWrap = el('div', {});
  if (state.error) {
    errorWrap.appendChild(el('div', { class: 'session-error', text: state.error }));
  }
  screen.appendChild(errorWrap);

  const actionsWrap = el('div', { class: 'actions' });
  screen.appendChild(actionsWrap);

  const captureInput = createCaptureInput({
    onPick: (file) => {
      pendingFile = file;
      if (pendingUrl) revokeObjectURL(pendingUrl);
      pendingUrl = fileToObjectURL(file);
      renderActions();
    },
  });
  screen.appendChild(captureInput);

  function startRetake() {
    pendingFile = null;
    if (pendingUrl) revokeObjectURL(pendingUrl);
    pendingUrl = null;
    pendingRoom = '';
    clearSlot('ap');
    captureInput.click();
  }

  function renderActions() {
    bodyWrap.replaceChildren();
    actionsWrap.replaceChildren();

    if (state.uploading) {
      actionsWrap.appendChild(
        el('div', { class: 'uploading' }, [el('div', { class: 'spinner' }), copy.uploading]),
      );
      return;
    }

    // Have a recognition result waiting for commit/retake.
    if (pendingAp) {
      bodyWrap.appendChild(
        resultCard({
          recognized: pendingAp.recognized,
          extraction: pendingAp.extraction,
          resultCopy: copy.result,
        }),
      );
      if (pendingAp.recognized) {
        bodyWrap.appendChild(
          el('div', { class: 'field' }, [
            el('label', { for: 'ap-room', text: copy.ap.roomLabel }),
            el('input', {
              id: 'ap-room',
              type: 'text',
              placeholder: copy.ap.roomPlaceholder,
              autocomplete: 'off',
              value: pendingAp.room ?? '',
              onInput: (e) => {
                pendingAp.room = e.target.value;
              },
            }),
          ]),
        );
        actionsWrap.appendChild(
          el('button', {
            class: 'primary',
            text: copy.ap.add,
            onClick: () => {
              commitPendingAp();
              pendingFile = null;
              if (pendingUrl) revokeObjectURL(pendingUrl);
              pendingUrl = null;
              pendingRoom = '';
            },
          }),
        );
        actionsWrap.appendChild(
          el('button', { class: 'secondary', text: copy.ap.retake, onClick: startRetake }),
        );
      } else {
        actionsWrap.appendChild(
          el('button', { class: 'primary', text: copy.ap.retake, onClick: startRetake }),
        );
      }
      // Always-visible escape hatch.
      actionsWrap.appendChild(
        el('button', {
          class: 'skip',
          text: state.photos.aps.length > 0 ? copy.ap.done : copy.ap.skip,
          onClick: () => goto('done'),
        }),
      );
      return;
    }

    // Have a preview but not uploaded yet.
    if (pendingFile && pendingUrl) {
      bodyWrap.appendChild(el('img', { class: 'preview', src: pendingUrl, alt: 'Preview' }));
      actionsWrap.appendChild(
        el('button', {
          class: 'primary',
          text: copy.ap.usePhoto,
          onClick: async () => {
            clearError();
            await submitPhoto({ slot: 'ap', file: pendingFile, room: pendingRoom || null });
            pendingFile = null;
            if (pendingUrl) revokeObjectURL(pendingUrl);
            pendingUrl = null;
          },
        }),
      );
      actionsWrap.appendChild(
        el('button', {
          class: 'secondary',
          text: copy.ap.retake,
          onClick: () => {
            pendingFile = null;
            if (pendingUrl) revokeObjectURL(pendingUrl);
            pendingUrl = null;
            captureInput.click();
          },
        }),
      );
      return;
    }

    // Cold-start: no pending capture.
    actionsWrap.appendChild(
      el('button', {
        class: 'primary',
        text: copy.ap.take,
        onClick: () => captureInput.click(),
      }),
    );
    actionsWrap.appendChild(
      el('button', {
        class: 'secondary',
        text: state.photos.aps.length > 0 ? copy.ap.done : copy.ap.skip,
        onClick: () => goto('done'),
      }),
    );
  }

  renderActions();
  return screen;
}
